import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import GeoSuggestions from '../components/GeoSuggestions';

// Cross-site GEO measurement board: which sites are measured, how many
// prompts each has, and Claude prompt suggestions for many sites at once.

const SKIP_LABELS = {
  no_keywords: 'אין מילות מפתח',
  has_pending: 'יש הצעות ממתינות',
  full: 'הסט מלא',
};

function shortDate(iso) {
  return iso ? new Date(iso + 'Z').toLocaleDateString('he-IL') : '—';
}

export default function GeoOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [open, setOpen] = useState(null);
  const [count, setCount] = useState(10);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => (
    api.getGeoOverview().then(setData).catch(err => setMessage({ type: 'error', text: err.message }))
  ), []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="loading">{message ? message.text : 'טוען...'}</div>;

  const sites = data.sites.filter(s => !search.trim() || s.site_name.toLowerCase().includes(search.trim().toLowerCase()));
  const eligible = sites.filter(s => !s.skip_reason);
  const chosen = eligible.filter(s => selected[s.site_id]);
  const allChosen = eligible.length > 0 && chosen.length === eligible.length;

  async function act(fn) {
    setBusy(true);
    setMessage(null);
    try { await fn(); await load(); }
    catch (err) { setMessage({ type: 'error', text: err.message }); }
    setBusy(false);
  }

  function generate() {
    if (!window.confirm('לבקש הצעות פרומפטים ל-' + chosen.length + ' אתרים (עד ' + count + ' לאתר)? ההצעות ימתינו לאישור ולא ייבדקו מול המנועים עד שיאושרו.')) return;
    act(async () => {
      const res = await api.generateGeoSuggestions(chosen.map(s => s.site_id), count);
      setSelected({});
      setMessage({ type: 'success', text: 'ההצעות בהכנה ל-' + res.queued + ' אתרים — רעננו בעוד דקה או שתיים.' });
    });
  }

  function enable(s) {
    const calls = s.active_prompts * s.engines;
    if (!window.confirm('להפעיל מדידה שבועית ל-' + s.site_name + '? כל שבוע ירוצו ' + calls + ' בדיקות בתשלום (' + s.active_prompts + ' פרומפטים × ' + s.engines + ' מנועים).')) return;
    act(() => api.updateGeoProject(s.site_id, { enabled: true }));
  }

  return (
    <>
      <div style={{ margin: '4px 2px 20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text)' }}>מדידת GEO — כל האתרים</h1>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '3px' }}>
          הצעת פרומפטים לכמה אתרים בבת אחת. ההצעות ממתינות לאישור באתר, והמדידה מופעלת בנפרד.
        </div>
      </div>

      {message && (
        <div style={{
          background: message.type === 'error' ? 'var(--red-glow)' : 'var(--green-glow)',
          border: '1px solid ' + (message.type === 'error' ? 'var(--red)' : 'var(--green)'),
          borderRadius: 'var(--radius-sm)', padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: message.type === 'error' ? 'var(--red)' : 'var(--green)',
        }}>{message.text}</div>
      )}

      <div className="filter-row" style={{ alignItems: 'center' }}>
        <div className="topbar-search" style={{ width: '260px' }}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="7" cy="7" r="5"/><path d="m11 11 3 3"/>
          </svg>
          <input type="text" placeholder="חיפוש אתר..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>הצעות לאתר:</span>
        <input className="geo-input" type="number" min={1} max={data.max_prompts} value={count} style={{ width: 70 }}
          onChange={e => setCount(Math.max(1, Math.min(data.max_prompts, parseInt(e.target.value, 10) || 1)))} />
        <button className="btn btn-primary" disabled={busy || !chosen.length} onClick={generate}>
          הצעת פרומפטים ל-{chosen.length} אתרים
        </button>
        <button className="btn btn-secondary" disabled={busy} onClick={() => act(async () => {})}>רענון</button>
      </div>

      <div className="sites-table-wrapper animate-in">
        <table className="sites-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" checked={allChosen} disabled={!eligible.length} style={{ accentColor: 'var(--accent)' }}
                  onChange={e => setSelected(e.target.checked ? Object.fromEntries(eligible.map(s => [s.site_id, true])) : {})} />
              </th>
              <th>אתר</th>
              <th>מדידה</th>
              <th>פרומפטים</th>
              <th>הצעות ממתינות</th>
              <th>מילות מפתח</th>
              <th>ריצה אחרונה</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sites.length === 0 && (
              <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px' }}>אין אתרים להצגה</td></tr>
            )}
            {sites.map(s => (
              <React.Fragment key={s.site_id}>
                <tr>
                  <td>
                    <input type="checkbox" checked={!s.skip_reason && !!selected[s.site_id]} disabled={!!s.skip_reason}
                      title={SKIP_LABELS[s.skip_reason] || ''} style={{ accentColor: 'var(--accent)' }}
                      onChange={e => setSelected({ ...selected, [s.site_id]: e.target.checked })} />
                  </td>
                  <td style={{ fontWeight: 600 }}>{s.site_name}</td>
                  <td style={{ color: s.enabled ? 'var(--green)' : 'var(--text-muted)' }}>{s.enabled ? 'פעילה' : 'כבויה'}</td>
                  <td style={{ fontFamily: 'var(--mono)' }}>{s.active_prompts}/{data.max_prompts}</td>
                  <td>
                    {s.pending_suggestions
                      ? <button className="btn-sm" onClick={() => setOpen(open === s.site_id ? null : s.site_id)}>{s.pending_suggestions} לאישור</button>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', color: s.keywords ? 'inherit' : 'var(--red)' }}>{s.keywords}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{shortDate(s.last_run)}</td>
                  <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                    {!s.enabled && s.active_prompts > 0 && (
                      <button className="btn-sm" disabled={busy} onClick={() => enable(s)} style={{ marginInlineEnd: 4 }}>הפעלת מדידה</button>
                    )}
                    <button className="btn-sm" onClick={() => navigate('/sites/' + s.site_id + '/geo-measurement')}>לאתר</button>
                  </td>
                </tr>
                {open === s.site_id && (
                  <tr>
                    <td colSpan="8" style={{ padding: '14px 18px', background: 'var(--bg-secondary, transparent)' }}>
                      <GeoSuggestions siteId={s.site_id} onChanged={load} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
